import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function isInvoiceInSelectedFys(inv: any, selectedFys: string[]): boolean {
  if (!selectedFys || selectedFys.length === 0) return true;
  if (inv.financial_year) {
    return selectedFys.includes(inv.financial_year);
  }
  const d = new Date(inv.Date);
  if (isNaN(d.getTime())) return false;
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed: 0 = Jan
  let fy = "";
  if (month >= 3) { // Apr or later
    fy = `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
  } else {
    fy = `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
  }
  return selectedFys.includes(fy);
}

interface TdsReceivableRecord {
  id: number;
  Date: string;
  InvoiceNo: string;
  company_name: string;
  customer_name: string;
  pan_number: string;
  Base_Amount: number;
  TDSAmount: number;
  TDS_Rate: number;
}

const TdsReceivableReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<TdsReceivableRecord[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    // Load companies and customers list
    Promise.all([
      axios.get(`${API_URL}/auth/companies`),
      axios.get(`${API_URL}/auth/customers`)
    ])
      .then(([compRes, custRes]) => {
        setCompanies(compRes.data || []);
        setCustomers(custRes.data || []);
      })
      .catch(() => setErrMsg("Failed to load metadata."));
  }, []);

  const fetchTdsReceivableData = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const [invoicesRes, settingRes] = await Promise.all([
        axios.get(`${API_URL}/auth/invoices`),
        axios.get(`${API_URL}/auth/setting`),
      ]);
      let invoices = invoicesRes.data || [];
      const settingData = settingRes.data || {};

      let selectedFys: string[] = [];
      try {
        if (settingData.selected_fys) {
          selectedFys = JSON.parse(settingData.selected_fys);
        }
      } catch (e) {}

      if (selectedFys.length > 0) {
        invoices = invoices.filter((inv: any) => isInvoiceInSelectedFys(inv, selectedFys));
      }

      // Filter and map TDS Receivable records
      const records: TdsReceivableRecord[] = invoices
        .filter((inv: any) => {
          if (inv.cancelled === 1 || inv.cancelled === true) return false;
          if (selectedCompanyId && String(inv.Company_id) !== selectedCompanyId) return false;
          if (selectedCustomerId && String(inv.Customer_id) !== selectedCustomerId) return false;
          if (startDate && new Date(inv.Date) < new Date(startDate)) return false;
          if (endDate && new Date(inv.Date) > new Date(endDate)) return false;
          return Number(inv.TDSAmount) > 0;
        })
        .map((inv: any) => {
          const customer = customers.find((c: any) => String(c.id) === String(inv.Customer_id));
          const comp = companies.find((c: any) => String(c.id) === String(inv.Company_id));
          
          // Reconstruct base amount: base = total - sgst - cgst - igst
          const baseAmt = Number(inv.Total_Amount) - Number(inv.SGSTAmount || 0) - Number(inv.CGSTAmount || 0) - Number(inv.IGSTAmount || 0);
          const tdsRate = baseAmt > 0 ? (Number(inv.TDSAmount) * 100) / baseAmt : 0;

          return {
            id: inv.id,
            Date: inv.Date,
            InvoiceNo: inv.InvoiceNo,
            company_name: comp ? comp.company_name : "Unknown",
            customer_name: customer ? customer.company_name : "Unknown",
            pan_number: customer ? customer.pancard || "—" : "—",
            Base_Amount: baseAmt,
            TDSAmount: Number(inv.TDSAmount),
            TDS_Rate: Math.round(tdsRate * 10) / 10
          };
        });

      setReport(records);
    } catch (err) {
      setErrMsg("Failed to retrieve invoice data for TDS calculation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companies.length > 0 && customers.length > 0) {
      fetchTdsReceivableData();
    }
  }, [companies, customers, selectedCompanyId, selectedCustomerId, startDate, endDate]);

  const totalBase = report.reduce((sum, item) => sum + item.Base_Amount, 0);
  const totalTds = report.reduce((sum, item) => sum + item.TDSAmount, 0);

  return (
    <div className="max-w-7xl mx-auto py-10 px-4">
      <div className="mb-8">
        <h2 className="text-3xl font-extrabold text-blue-800">TDS Receivable Report</h2>
        <p className="text-gray-600 mt-2">Overview of TDS amounts deducted by customers on our sales invoices.</p>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow mb-8">
        <div className="grid md:grid-cols-4 gap-6 items-end">
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">Company</label>
            <select
              value={selectedCompanyId}
              onChange={e => setSelectedCompanyId(e.target.value)}
              className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            >
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">Customer</label>
            <select
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            >
              <option value="">All Customers</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="form-input w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="form-input w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            />
          </div>
        </div>
      </div>

      {errMsg && <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-xl border border-red-200 text-center shadow font-semibold">{errMsg}</div>}

      {/* Summary Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6 shadow-md">
          <div className="text-sm font-semibold text-blue-800 uppercase tracking-wider">Total Sales base Value</div>
          <div className="text-3xl font-extrabold text-blue-900 mt-2">₹ {totalBase.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-pink-50 border border-red-100 rounded-2xl p-6 shadow-md">
          <div className="text-sm font-semibold text-red-800 uppercase tracking-wider">Total TDS Receivable</div>
          <div className="text-3xl font-extrabold text-red-900 mt-2">₹ {totalTds.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : report.length === 0 ? (
        <div className="text-center py-10 bg-white shadow rounded-2xl border text-gray-500">No TDS Receivable records found for selected criteria.</div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-xl rounded-2xl border">
          <table className="min-w-full text-left border-collapse">
            <thead>
              <tr className="bg-blue-50 border-b">
                <th className="p-4 font-bold text-blue-900">Date</th>
                <th className="p-4 font-bold text-blue-900">Invoice No</th>
                <th className="p-4 font-bold text-blue-900">Company</th>
                <th className="p-4 font-bold text-blue-900">Customer</th>
                <th className="p-4 font-bold text-blue-900">PAN Card</th>
                <th className="p-4 font-bold text-blue-900 text-right">Taxable base (₹)</th>
                <th className="p-4 font-bold text-blue-900 text-right">Rate</th>
                <th className="p-4 font-bold text-blue-900 text-right">TDS (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {report.map(record => (
                <tr key={record.id} className="hover:bg-blue-50/20 transition-colors">
                  <td className="p-4 whitespace-nowrap">{new Date(record.Date).toLocaleDateString("en-GB")}</td>
                  <td className="p-4 font-semibold text-gray-700">{record.InvoiceNo}</td>
                  <td className="p-4">{record.company_name}</td>
                  <td className="p-4 font-medium">{record.customer_name}</td>
                  <td className="p-4 font-mono text-gray-600">{record.pan_number}</td>
                  <td className="p-4 text-right font-medium">₹ {record.Base_Amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right text-gray-600 font-bold">{record.TDS_Rate}%</td>
                  <td className="p-4 text-right font-bold text-red-600">₹ {record.TDSAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TdsReceivableReport;
